import { MapViewerNode, Viewer3dRelatedPreviewObject } from '@3ddv/dvm-internal';

import { ChangeDetectorRef, Component, computed, DestroyRef, effect, EffectRef, ElementRef, inject, signal, OnInit, Signal, ViewChild, WritableSignal } from '@angular/core';
import { DvmFormatterPipe } from '@helpers/pipes/dvm-formatter.pipe';
import { AppNodes } from '@models/enums/dvm/DvmNodes.enum';
import { AppViews } from '@models/enums/dvm/DvmViews.enum';
import { NeighborComponent } from '@models/interfaces/components/Neighbor.interface';
import { UiConfiguration } from '@models/interfaces/configuration/UiConfiguration.interface';
import { GeneralAdmission } from '@models/types/components/GeneralAdmission.type';
import { Neighbor, NeighborRecord, RelatedDirection, RelatedMapCandidate, RelatedMaps } from '@models/types/components/Neighbor.type';
import { Translation } from '@models/types/components/Translation.type';
import { AmenitiesService } from '@services/amenities.service';
import { RelatedPreviewsService } from '@services/components/related-previews.service';
import { DvmService } from '@services/dvm.service';
import { FilterService } from '@services/filter.service';
import { TranslationService } from '@services/translation.service';
import { UiService } from '@services/ui.service';
import anime, { AnimeInstance } from 'animejs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ViewerRouteParamsService } from '@services/viewer-route-params.service';
import { isInteriorViewId } from '@helpers/utils/dvm-node.util';

@Component({
    selector: 'app-neighbors',
    templateUrl: './neighbors.component.html',
    styleUrl: './neighbors.component.css',
    standalone: true,
    imports: [
    DvmFormatterPipe
],
    providers: [DvmFormatterPipe]
})
export class NeighborsComponent implements NeighborComponent, OnInit  {

  // VIEWCHILD
  @ViewChild('neighborsContainer')
  public readonly  neighborsContainer!:   ElementRef<HTMLDivElement>;

  @ViewChild('currentSection', { static: false })
  public readonly  currentSection!:       ElementRef<HTMLSpanElement>;
  
  @ViewChild('currentSection3d', { static: false })
  public readonly  currentSection3d!:     ElementRef<HTMLSpanElement>;

  @ViewChild('leftSectionNumber', { static: false })
  public readonly  leftSectionElement!:   ElementRef<HTMLSpanElement>;

  @ViewChild('rightSectionNumber', { static: false })
  public readonly  rightSectionElement!:  ElementRef<HTMLSpanElement>;


  // SERVICES
  private readonly uiconfig:                Signal<UiConfiguration> = inject(UiService).uiConfig;
  private readonly dvmService:              DvmService              = inject(DvmService);
  private readonly amsService:              AmenitiesService        = inject(AmenitiesService);
  private readonly filterService:           FilterService           = inject(FilterService);
  private readonly relatedPreviewsService:  RelatedPreviewsService  = inject(RelatedPreviewsService);
  private readonly cdr:                     ChangeDetectorRef       = inject(ChangeDetectorRef);
  private readonly dvmFormatter:            DvmFormatterPipe        = inject(DvmFormatterPipe);
  private readonly routeParams:             ViewerRouteParamsService = inject(ViewerRouteParamsService);
  public  readonly translation:             TranslationService      = inject(TranslationService);
  private readonly destroyRef:              DestroyRef              = inject(DestroyRef);


  // CONFIGURATION
  private readonly config:                Signal<Neighbor>         = computed((): Neighbor => this.uiconfig()['Neighbors']);
  public  readonly data:                  Signal<Neighbor['data']> = computed((): Neighbor['data'] => this.config().data);
  public  readonly enabled:               Signal<boolean>          = computed((): boolean  => this.config().enabled);
  public  readonly modeLr:                Signal<boolean>          = computed((): boolean  => this.config().modeLr);
  public  readonly gaExceptions:          Signal<GeneralAdmission> = computed((): GeneralAdmission=> this.uiconfig().GeneralAdmission);
  public  readonly seatmapDisplayEnabled: Signal<boolean>          = computed((): boolean => this.uiconfig().SeatmapDisplay.enabled);
    
  
  
  //TRANSLATION CONFIG
  private readonly translationConfig:     Signal<Translation>         = computed((): Translation => this.uiconfig()['Translation']);
  public  readonly translationData:       Signal<Translation['data']> = computed((): Translation['data'] => this.translationConfig().data);
  public  readonly translationEnabled:    Signal<boolean>             = computed((): boolean  => this.translationConfig().enabled);


  // GLOBAL STATE
  private readonly   view:                Signal<AppViews|null>    = this.dvmService.view;
  protected readonly viewId:              string | null            = this.routeParams.viewId;
  private   readonly relatedPreviewLabel: string | null            = this.routeParams.relatedPreview;



  // COMPONENT STATE
  protected readonly isLoading3d:         WritableSignal<boolean>                     = signal(false);
  protected readonly showElement:         WritableSignal<boolean>                     = signal(false);
  protected readonly currentPreview:      Signal<Viewer3dRelatedPreviewObject | null> = this.relatedPreviewsService.currentPreview;
  protected initialViewId:                string | null                               = null;
  protected toggleAnimation:              AnimeInstance | null                        = null;
  private pendingAnimatedSection:         string | null                               = null;


  // SECTION DATA
  protected readonly selectedSection:     WritableSignal<string | null>          = signal(null);
  protected readonly sectionData:         WritableSignal<NeighborRecord | null>  = signal(null);

  // EFFECTS
  private onViewChange:                   EffectRef                = effect((): void =>{

    const view: AppViews | null = this.view();

    if(view === null) return;

    if(!this.enabled()) {
      if(this.showElement() || this.toggleAnimation) this.toggleNeighbors(false);
      this.applyCurrentSection(null);
      return;
    }

    this.initialViewId = null;
    
    switch(view){
      
      // Seteamos sección, si está oculto mostramos elemento y comprobamos si el texto seatmap es false para toglearlo.
      case AppViews.Seatmap:
        
        this.applyCurrentSection();
        
        if(!this.showElement() || this.toggleAnimation) this.toggleNeighbors(true);
        
        break;
        
        // Seteamos sección en base al viewer3d, si está oculto mostramos elemento y comprobamos si el texto 3d es false para toglearlo.
        case AppViews.Pano:
        
        this.applyCurrentSection(this.dvmService.viewer3d.getViewId());

        if(!this.showElement() || this.toggleAnimation) this.toggleNeighbors(true);
        
        this.initialViewId = this.dvmService.viewer3d.getViewId();

        this.setCurrentPreview();

        
      break;

      // 
      default:

        if(this.showElement() || this.toggleAnimation){

          this.toggleNeighbors(false);

          window.setTimeout((): void => this.applyCurrentSection(null), 300);

        }

      break;

    }

  });

  // GETTERS
  /**
   * Devuelve la etiqueta visible para la flecha izquierda.
   *
   * Oculta la flecha si no hay destino valido, si la vista actual es 3D, si el destino no pasa filtros,
   * si SeatmapDisplay esta deshabilitado o si la seccion actual/destino es una excepcion GA.
   *
   * @returns Texto traducido/formateado de la seccion izquierda, o `null` si no debe mostrarse.
   */
  public get leftSection(): string | null {
    return this.getNeighborLabel('l');
  }


  /**
   * Devuelve la etiqueta visible para la flecha derecha.
   *
   * Aplica las mismas reglas de visibilidad que `leftSection`, pero usando el destino derecho.
   *
   * @returns Texto traducido/formateado de la seccion derecha, o `null` si no debe mostrarse.
   */
  public get rightSection(): string | null {
    return this.getNeighborLabel('r');
  }

  /**
   * Indica si la vista actual del minisite corresponde al viewer 3D.
   *
   * @returns `true` cuando la vista activa es `AppViews.Pano`.
   */
  public get is3d(): boolean {

    return this.view() === AppViews.Pano;

  }

  /**
   * Indica si la ubicacion actual debe tratarse visualmente como premium.
   *
   * Conserva el comportamiento AMS existente: una amenity seleccionada o nodos con grupo `premium`
   * activan el formato dorado/blanco de la ubicacion.
   *
   * @returns `true` si hay amenities seleccionadas o asientos premium en el mapa.
   */
  public get sectionIsPremium(): boolean {

    const hasAmenities:    boolean = this.amsService.hasSelectedAmenities(),
          hasPremiumSeats: boolean = this.dvmService.viewer.getNodes().some(node => node.groups.includes('premium'));

    return hasAmenities || hasPremiumSeats;

  }

  /**
   * Comprueba si una seccion o vista debe comportarse como General Admission.
   *
   * Si no se pasa id, usa el `view_id` 3D actual para mantener el comportamiento previo en pano.
   *
   * @param viewId Id de seccion o vista a comprobar.
   * @returns `true` si GA esta habilitado y el id esta en la lista configurada.
   */
  public isGaException(viewId?: string): boolean {
    const sectionId = viewId ?? this.dvmService.viewer3d.getViewId();
    return !!sectionId && this.gaExceptions().enabled && this.gaExceptions().data.includes(sectionId);
  }


  /**
   * Inicializa las suscripciones del componente a eventos DVM.
   */
  ngOnInit(): void {
    this.initComponent();
  }

  // METHODS
  /**
   * Navega a una seccion lateral y actualiza el estado visible tras cargar el mapa.
   *
   * Mantiene la integracion AMS llamando a `checkSectionAmenity` con el `original_id`
   * del mapa cargado despues de `DvmService.loadMap`.
   *
   * @param section Id del mapa/seccion destino.
   */
  public navigateToSection(section: MapViewerNode['id']): void {
    
    if(!this.isValidDestination(section)) return; // Si no hay sección, no hacemos nada

    this.dvmService.loadMap(section).subscribe({
      next: (): void => {
        this.setCurrentSection(section);
        this.amsService.checkSectionAmenity(this.dvmService.viewer.getNodeById(this.dvmService.currentMap!)!.original_id);
      },
      error: (error: unknown): void => console.error('Error loading neighbor section', error)
    });
    
  }

  /**
   * Recalcula la seccion actual usando un id explicito o el mapa actual del DVM.
   *
   * @param sectionId Id de seccion a aplicar. Si es `undefined`, usa `DvmService.currentMap`; si es `null`, limpia la seccion.
   */
  private setCurrentSection(sectionId?: MapViewerNode['id'] | null): void {
    this.applyCurrentSection(sectionId);
  }

  /**
   * Aplica la ubicacion central y los datos laterales para el estado actual.
   *
   * En seatmap resuelve los vecinos desde archivo legacy o desde `viewer.related.getRelatedMaps()`.
   * En pano elimina datos laterales y formatea el `view_id` 3D. Cuando ya hay una seccion visible,
   * anima el cambio para evitar saltos bruscos en el topbar.
   *
   * @param sectionId Id de seccion/vista a aplicar. `undefined` usa el mapa actual; `null` limpia el estado.
   */
  private applyCurrentSection(sectionId?: MapViewerNode['id'] | null): void {
    let section!: string | null;
    /**
     * El parametro sectionId puede ser null o undefined, si no es null, comprobamos
     * si es undefined. Si es undefined, tomamos el id del mapa actual, si no, lo tomamos del parametro.
     */
    if (sectionId !== null) {
      section =
        sectionId !== undefined ? sectionId : this.dvmService.currentMap;
    }

    const sectionData = section ? this.resolveSectionData(section) : null;

    // Si ya hay, animamos el cambio de sección
    if (this.selectedSection() && section && !this.viewId && !this.is3d) {
      
      this.animateSectionNumbers(section, sectionData);
    }
    // Si no hay, seteamos la sección y los datos
    else {
      if (!section) {
        this.selectedSection.set(section);
      }  else {
        const translated = !this.is3d && this.translationEnabled()
          ? this.translationData()[section]
          : null;

          if(this.viewId) { //display proper section format for when it is a copied link
            let separatorString = '&nbsp;';

            if((!this.isGaException() && this.seatmapDisplayEnabled())) {
              separatorString = '&nbsp; | &nbsp;';
            }

            // Always remove pipe for INT sections
            if (isInteriorViewId(section)) {
              separatorString = '&nbsp;';
            }

            this.selectedSection.set(translated ?? this.dvmFormatter.transform(section, true, false, true, separatorString));
          } else {
            this.selectedSection.set(translated ?? (this.is3d ? this.formatSectionDataForAmenity(section) : this.dvmFormatter.transform(section)));
          }
        }

      this.sectionData.set(this.is3d ? null : sectionData);

    }
    this.cdr.markForCheck();
  }

  /**
   * Actualiza solo el texto central de la ubicacion durante una animacion.
   *
   * @param section Id de seccion o vista que se mostrara.
   * @param is3d Indica si debe aplicarse el formato especifico de pano/AMS.
   */
  private applySection(section: string, is3d: boolean = false): void {
      const translated = this.translationEnabled() ? this.translationData()[section] : null;

      if(translated) {
        this.selectedSection.set(this.translationData()[section]);
      } else if (!is3d) {
         this.selectedSection.set(this.dvmFormatter.transform(section));
      } else {
        this.selectedSection.set(this.formatSectionDataForAmenity(section));
      }
  }

  /**
   * Muestra u oculta el contenedor de Neighbors usando la animacion actual del componente.
   *
   * Cancela cualquier animacion previa antes de arrancar una nueva para evitar estados visuales inconsistentes.
   *
   * @param show `true` para mostrar el contenedor; `false` para ocultarlo.
   */
  private toggleNeighbors(show: boolean): void {

    if(this.toggleAnimation) {
      this.toggleAnimation.pause()
      this.toggleAnimation = null;
    }

    if(show && !this.showElement()){
      this.showElement.set(true);
      this.cdr.markForCheck();
    }
    
    this.toggleAnimation = anime({
      targets: this.neighborsContainer.nativeElement,
      translateY: show ? 0 : 50,
      opacity: show ? 1 : 0,
      duration: 300,
      easing: 'easeInOutQuad',
      complete: () => {
        if(!show){
          this.showElement.set(false);
          this.cdr.markForCheck();
        }
        this.toggleAnimation = null;
      }
    })


  }
  
  /**
   * Anima el cambio de numero de seccion y actualiza los datos laterales a mitad de la animacion.
   *
   * @param section Id de la nueva seccion.
   * @param sectionData Destinos laterales resueltos para la nueva seccion.
   */
  private animateSectionNumbers(section: string, sectionData: NeighborRecord | null): void {
    
    const targets: HTMLElement[] = [
      this.currentSection?.nativeElement ?? null, 
      this.currentSection3d?.nativeElement ?? null,
      this.leftSectionElement?.nativeElement ?? null, 
      this.rightSectionElement?.nativeElement ?? null
    ].filter(el => el !== null) as HTMLElement[];

    anime({
      targets:  targets,
      duration: 500,
      filter:   ['blur(0px)', 'blur(2px)', 'blur(0px)'],
      easing:   'easeInOutQuad',
      update:   (animation): void => {
  
        if(animation.progress >= 50 && this.selectedSection() !== section.split('_')[1] && this.pendingAnimatedSection !== section){
          this.pendingAnimatedSection = section;
          this.applySection(section, this.view() === AppViews.Pano);
          this.sectionData.set(sectionData);
          this.pendingAnimatedSection = null;
          this.cdr.markForCheck();
        }
        
      }
    })

  }

  /**
   * Resuelve los destinos laterales para una seccion.
   *
   * Si hay data legacy cargada, la usa de forma exclusiva. Si no hay archivo legacy,
   * consulta el plugin `related` mediante `getRelatedMaps()` y adapta sus objetos `left/right`
   * al formato interno `{ l, r }` que consume el template.
   *
   * @param section Id del mapa/seccion actual.
   * @returns Registro de vecinos para la seccion o `null` si no hay navegacion lateral.
   */
  private resolveSectionData(section: string): NeighborRecord | null {
    if(this.hasLegacyData()) return this.data()[section] ?? null;

    return this.resolveRelatedSectionData();
  }

  /**
   * Resuelve los vecinos desde `viewer.related.getRelatedMaps()`.
   *
   * El SDK puede exponer `related` en runtime aunque no siempre este tipado en la libreria.
   * Por eso se valida la forma del resultado antes de usar `map_id`.
   *
   * @returns Registro `{ l, r }` derivado de `left/right`, o `null` si no hay destinos.
   */
  private resolveRelatedSectionData(): NeighborRecord | null {
    const relatedMaps = this.getRelatedMaps(),
          left = relatedMaps?.left?.map_id,
          right = relatedMaps?.right?.map_id;

    if(!left && !right) return null;

    return {
      l: this.isValidDestination(left) ? left : 'none',
      r: this.isValidDestination(right) ? right : 'none'
    };
  }

  /**
   * Indica si la configuracion trae data legacy de neighbors.
   *
   * @returns `true` si `Ui.Neighbors.data` contiene al menos una entrada.
   */
  private hasLegacyData(): boolean {
    return Object.keys(this.data() ?? {}).length > 0;
  }

  /**
   * Lee todos los mapas relacionados del plugin `related`.
   *
   * Se usa exclusivamente `getRelatedMaps()` porque devuelve los objetos `left` y `right`
   * necesarios para mostrar informacion y navegar entre seatmaps.
   *
   * @returns Mapa parcial de destinos relacionados o `null` si el plugin/metodo no esta disponible.
   */
  private getRelatedMaps(): RelatedMaps | null {
    const related = this.dvmService.viewer.related;

    if(!related) return null;

    return this.extractRelatedMaps(related.getRelatedMaps?.());
  }

  /**
   * Normaliza la respuesta desconocida de `getRelatedMaps()` al shape usado por el componente.
   *
   * @param value Valor devuelto por el SDK.
   * @returns Objeto con posibles claves `left` y `right`, o `null` si no hay ningun destino valido.
   */
  private extractRelatedMaps(value: unknown): RelatedMaps | null {
    if(!value || typeof value !== 'object') return null;

    const candidate = value as Record<RelatedDirection, unknown>,
          left = this.extractRelatedMap(candidate.left),
          right = this.extractRelatedMap(candidate.right),
          relatedMaps: RelatedMaps = {};

    if(left) relatedMaps.left = left;
    if(right) relatedMaps.right = right;

    return left || right ? relatedMaps : null;
  }

  /**
   * Valida y extrae un objeto related individual.
   *
   * @param value Posible objeto `left` o `right` devuelto por el plugin.
   * @returns Destino related si contiene `map_id`; en caso contrario, `null`.
   */
  private extractRelatedMap(value: unknown): RelatedMapCandidate | null {
    if(!value || typeof value !== 'object') return null;

    const candidate = value as Record<string, unknown>;

    if(typeof candidate['map_id'] === 'string' && candidate['map_id']) return candidate as RelatedMapCandidate;

    return null;
  }

  /**
   * Comprueba si un destino lateral puede usarse para navegar o mostrarse.
   *
   * @param section Id candidato de seccion.
   * @returns `true` si el valor existe y no es el sentinel legacy `'none'`.
   */
  private isValidDestination(section?: string | null): section is string {
    return !!section && section !== 'none';
  }

  /**
   * Devuelve la etiqueta visible para un destino lateral.
   *
   * Centraliza la logica comun de izquierda/derecha: vista 3D, filtros, GA,
   * SeatmapDisplay, traduccion y truncado.
   *
   * @param direction Clave interna del vecino (`l` o `r`).
   * @returns Texto visible del vecino o `null` si la flecha debe ocultarse.
   */
  private getNeighborLabel(direction: keyof NeighborRecord): string | null {
    const sectionData: NeighborRecord | null = this.sectionData(),
          destination = sectionData?.[direction];

    if(!this.canShowNeighbor(destination)) return null;

    const translation = this.translationEnabled() && this.translationData()[destination]
      ? this.translationData()[destination]
      : destination;

    return this.truncateNeighborLabel(translation);
  }

  /**
   * Evalua si un destino lateral debe mostrarse segun el estado actual.
   *
   * @param destination Id de seccion destino.
   * @returns `true` si la flecha puede mostrarse.
   */
  private canShowNeighbor(destination?: string | null): destination is string {
    if (this.is3d || !this.isValidDestination(destination) || this.isGaException() || this.isGaException(destination)) return false;
    if (!this.seatmapDisplayEnabled()) return false;

    return !this.filterService.hasFilters() || this.filterService.getFilterByKey(AppNodes.Section).includes(destination);
  }

  /**
   * Recorta etiquetas largas para que el topbar mantenga el layout esperado.
   *
   * @param label Texto ya traducido o id de seccion.
   * @returns Texto original si cabe, o version truncada con puntos suspensivos.
   */
  private truncateNeighborLabel(label: string): string {
    return label.length > 20
      ? label.substring(0, 20) + '...'
      : label;
  }

  /**
   * Formatea una ubicacion 3D conservando el tratamiento visual AMS/premium.
   *
   * Si no hay estado premium, devuelve el formato normal del `DvmFormatterPipe`.
   * Si lo hay, envuelve labels y valores con las clases actuales `text-vip` y `text-white`.
   *
   * @param section Id de vista/seccion que se debe formatear.
   * @returns HTML seguro para el template actual con la ubicacion formateada.
   */
  private formatSectionDataForAmenity(section: string): string {
    const translationMap = this.translationEnabled() ? this.translationData() : undefined;
    const sectionData = this.dvmFormatter.transform(section, true, false, true, '&nbsp; | &nbsp;', translationMap);

    if (!this.sectionIsPremium) return sectionData;

    return sectionData
      .split('&nbsp; | &nbsp;')
      .map(item => {
        const parts = item.trim().split(' ');
        const label = parts[0];
        const value = parts.slice(1).join(' ');

        return `<span class="text-vip">${label}</span> <span class="text-white">${value}</span>`;
      })
      .join(' <span class="text-vip">&nbsp; | &nbsp;</span> ');
  }


  /**
   * Registra listeners del viewer 2D y 3D necesarios para mantener sincronizado el topbar.
   *
   * Espera la inicializacion del viewer 3D antes de suscribirse a eventos, y limpia todo con
   * `takeUntilDestroyed` al destruir el componente.
   */
  private initComponent(): void {

    const viewer3d = this.dvmService.viewer3d,
          viewer   = this.dvmService.viewer;

    viewer3d.waitInitialize().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((): void => {

      // LOAD SUCCESS
      viewer.getObservable('load_success').pipe(takeUntilDestroyed(this.destroyRef)).subscribe((): void => {
        if(this.view() !== AppViews.Pano) this.setCurrentSection();
      });

      // LOAD START
      viewer3d.getObservable('load_start').pipe(takeUntilDestroyed(this.destroyRef)).subscribe((): void => this.isLoading3d.set(true));

      // LOAD SUCCESS
      viewer3d.getObservable('load_success').pipe(takeUntilDestroyed(this.destroyRef)).subscribe((): void => {
        this.isLoading3d.set(false);
        if(this.view() === AppViews.Pano || this.viewId) this.applyCurrentSection(viewer3d.getViewId());
      });

      // LOAD ERROR
      viewer3d.getObservable('load_error').pipe(takeUntilDestroyed(this.destroyRef)).subscribe((): void => this.isLoading3d.set(false));

    });


  }

  /**
   * Restaura el preview relacionado cuando el usuario entra desde un enlace compartido.
   *
   * El listado real de related previews vive en DVM, asi que para enlaces directos se reconstruye
   * el preview activo con el label recibido en query param.
   */
  private setCurrentPreview(): void {

    if(!this.relatedPreviewLabel) return;
    
    const relatedPreview: Viewer3dRelatedPreviewObject = {
      id: this.viewId!,
      venue_id: this.dvmService.currentVenue!,
      view_id: this.viewId!,
      label: this.relatedPreviewLabel
    };

    this.initialViewId = null;
    this.relatedPreviewsService.currentPreview.set(relatedPreview);

  }

}
